import Image from "next/image";

import amexMark from "@assets/images/payment/amex.png";
import mastercardMark from "@assets/images/payment/mastercard.png";
import visaMark from "@assets/images/payment/visa.png";
import IconCheckCircle from "@assets/svg/IconCheckCircle";
import AffirmMark from "@assets/svg/payment/affirm.svg";
import AmazonPayMark from "@assets/svg/payment/amazon-pay.svg";
import DiscoverMark from "@assets/svg/payment/discover.svg";
import GooglePayMark from "@assets/svg/payment/google-pay.svg";
import PayPalMark from "@assets/svg/payment/paypal.svg";
import { PAYMENT_MARKS } from "@/chrome/chromeContent";
import { cn } from "@/lib/utils";

/* Figma names two layers after the wrong brand ("Image (Affirm)" draws Amex,
   "diners 1" draws Google Pay); these follow what the artwork shows. */

const MARK_HEIGHT = "h-[20px] tab:h-[30px] sd:h-[35px] d:h-[40px]";

const WIDTH_CARD = "w-[34px] tab:w-[50px] sd:w-[59px] d:w-[67px]";
const WIDTH_AMEX = "w-[37px] tab:w-[55px] sd:w-[64px] d:w-[74px]";
const WIDTH_WIDE = "w-[31px] tab:w-[47px] sd:w-[55px] d:w-[62px]";
const WIDTH_AMAZON = "w-[30px] tab:w-[45px] sd:w-[52px] d:w-[60px]";

/* In row order, after the HSA/FSA badge. Raster marks are node renders with
   Figma's crop already applied; do not re-derive it. */
const MARKS = [
  {
    kind: "vector",
    Mark: AffirmMark,
    alt: PAYMENT_MARKS.affirm,
    width: WIDTH_WIDE,
  },
  {
    kind: "raster",
    src: visaMark,
    alt: PAYMENT_MARKS.visa,
    width: WIDTH_CARD,
  },
  {
    kind: "raster",
    src: mastercardMark,
    alt: PAYMENT_MARKS.mastercard,
    width: WIDTH_CARD,
  },
  {
    kind: "raster",
    src: amexMark,
    alt: PAYMENT_MARKS.amex,
    width: WIDTH_AMEX,
  },
  {
    kind: "vector",
    Mark: DiscoverMark,
    alt: PAYMENT_MARKS.discover,
    width: WIDTH_WIDE,
  },
  {
    kind: "vector",
    Mark: PayPalMark,
    alt: PAYMENT_MARKS.paypal,
    width: WIDTH_WIDE,
  },
  {
    kind: "vector",
    Mark: GooglePayMark,
    alt: PAYMENT_MARKS.googlePay,
    width: WIDTH_WIDE,
  },
  {
    kind: "vector",
    Mark: AmazonPayMark,
    alt: PAYMENT_MARKS.amazonPay,
    width: WIDTH_AMAZON,
  },
] as const;

const PaymentMarks = () => (
  <ul
    aria-label={PAYMENT_MARKS.label}
    /* Wraps on narrow phones, where Figma's mobile row is wider than its own
       artboard. shrink-0 from sd so the counters beside it give way instead of
       the row dropping a mark. */
    className="flex w-fit max-w-full flex-wrap items-center gap-[4px] p-[2px] tab:gap-[3.75px] tab:p-[3px] sd:shrink-0 sd:gap-[4.375px] sd:p-[3.5px] d:gap-[5px] d:p-[4px]"
  >
    <li
      className={cn(
        "flex shrink-0 items-center gap-[1px] rounded-payment-mark bg-brand-white px-[3px] tab:gap-[1.5px] tab:px-[4.5px] sd:gap-[1.75px] sd:px-[5.25px] d:gap-[2px] d:px-[6px]",
        MARK_HEIGHT,
      )}
    >
      <IconCheckCircle className="size-[9px] shrink-0 text-brand-pure-black tab:size-[14px] sd:size-[16px] d:size-[18px]" />
      <span className="font-ppMori text-payment-mark font-semibold text-brand-pure-black">
        {PAYMENT_MARKS.hsaFsa}
      </span>
    </li>

    {MARKS.map((mark) => (
      <li key={mark.alt} className={cn("shrink-0", MARK_HEIGHT, mark.width)}>
        {mark.kind === "raster" ? (
          <Image
            src={mark.src}
            alt={mark.alt}
            // The corner radius is baked into the export.
            className="h-full w-full object-contain"
          />
        ) : (
          <mark.Mark
            role="img"
            aria-label={mark.alt}
            className="h-full w-full"
          />
        )}
      </li>
    ))}
  </ul>
);

export default PaymentMarks;
